import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import { FormattedMessage } from 'react-intl';
import { Form, Input, InputNumber, Modal, Space } from 'antd';
import ActiveInput from 'components/ActiveInput';
import InputInfoCircle from 'components/InputInfoCircle';
import Table from 'components/Table';
import useFormatMessage from 'hooks/useFormatMessage';
import RemoveInput from 'components/RemoveInput';
import messages from './messages';

const OPTION_RESPONSE_COUNT = gql`
  query($id: ID!) {
    option(id: $id) { id responseCount }
  }
`;

const RemoveOptionModal = ({ option, onRemove, onCancel }) => {
  const { data, loading } = useQuery(OPTION_RESPONSE_COUNT, { variables: { id: option.id }, skip: !option });

  const responseCount = data?.option?.responseCount;

  const canRemove = responseCount === 0;

  return (
    <Modal
      title={<FormattedMessage {...messages.removeOptionTitle} />}
      open={option}
      onCancel={onCancel}
      onOk={canRemove ? onRemove : onCancel}
      confirmLoading={loading}
    >
      {!loading && (
        canRemove ? (
          <FormattedMessage {...messages.removeOptionNoResponses} values={{ option: option.title }} />
        ) : (
          <FormattedMessage {...messages.removeOptionHasResponses} values={{ option: option.title, responseCount }} />
        )
      )}
    </Modal>

  );
};

const EditOptions = ({ name, fieldName = [], isTranslation = false, showValue = false }) => {
  const $t = useFormatMessage();
  const [newOptionTitle, setNewOptionTitle] = useState('');
  const [optionToRemove, setOptionToRemove] = useState(false);

  const handleNewOption = (add) => {
    if (newOptionTitle) {
      add({ title: newOptionTitle.trim() });
      setNewOptionTitle('');
    }
  };

  const onRemove = (remove, field, value) => {
    if (value?.id) {
      setOptionToRemove({ id: value.id, title: value.title, name: field.name });
    } else {
      remove(field.name);
      setOptionToRemove(false);
    }
  };

  const columns = [{
    dataIndex: 'title',
    title: 'Title',
    className: 'drag-visible',
    render: (_, field, rowIndex) => (
      <Form.Item name={[field.name, ...fieldName, 'title']} rules={isTranslation ? [] : [{ required: true, message: $t(messages.optionTitleRequired, { row: rowIndex + 1 }) }]}>
        <Input />
      </Form.Item>
    ),
  }];

  if (showValue) {
    columns.push({
      dataIndex: 'value',
      width: 120,
      title: (
        <Space>
          Value
          <InputInfoCircle title="Assign a value to each option to calculate a score for the aggregated result." />
        </Space>
      ),
      render: (_, field) => (
        <Form.Item name={[field.name, ...fieldName, 'value']}>
          <InputNumber />
        </Form.Item>
      ),
    });
  }

  return (
    <Form.List name={name}>
      {(fields, { add, remove, move }) => (
        <>
          <Table
            rowKey="key"
            emptyMessage={isTranslation ? 'No options to translate' : 'Add options below'}
            pagination={false}
            sortable={!isTranslation}
            onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
            columns={columns}
            dataSource={fields}
            actions={field => (
              <Space>
                <Form.Item noStyle name={[field.name, ...fieldName, 'active']}>
                  <ActiveInput />
                </Form.Item>
                {!isTranslation && <RemoveInput onRemove={(value) => onRemove(remove, field, value)} />}
              </Space>
            )}
          />
          {!isTranslation && (
            <>
              <Input
                placeholder={$t(messages.addOptionPlaceholder)}
                value={newOptionTitle}
                onChange={e => setNewOptionTitle(e.target.value)}
                onPressEnter={() => handleNewOption(add)}
                suffix={<InputInfoCircle title={<FormattedMessage {...messages.addOptionTooltip} />} />}
              />
              <RemoveOptionModal option={optionToRemove} onRemove={() => onRemove(remove, optionToRemove)} onCancel={() => setOptionToRemove(false)} />
            </>
          )}
        </>
      )}
    </Form.List>
  );
};

export default EditOptions;
